import { h } from 'preact'
import { useRef, useEffect } from 'preact/hooks'
import Message from './Message'
import PropertyCards from './PropertyCards'

const QUICK_ACTIONS = [
  ['Find a rental', "I'm looking for a vacation rental in Sunriver"],
  ['Pet-friendly', 'Show me pet-friendly properties'],
  ['Hot tub homes', 'I want a home with a hot tub'],
  ['Check dates', 'Can you check availability for me?']
]

export default function ChatPanel({
  phone,
  phoneTel,
  sms,
  smsTel,
  email,
  redesign = false,
  isOpen,
  messages,
  isStreaming,
  isLoading,
  showGreeting,
  toolActivity,
  messageCount,
  maxMessages,
  inputDisabled,
  onClose,
  onReset,
  onSend,
  onQuickAction,
  onFeedback,
  suggestions,
  searchSuffix,
  streamElRef,
  scrollNonce
}) {
  const messagesRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 350)
    }
  }, [isOpen])

  useEffect(() => {
    scrollToBottom()
  }, [messages, toolActivity, isStreaming, scrollNonce])

  function scrollToBottom() {
    if (messagesRef.current) {
      requestAnimationFrame(() => {
        if (messagesRef.current) {
          messagesRef.current.scrollTop = messagesRef.current.scrollHeight
        }
      })
      // Re-apply after layout settles and again after images/cards render;
      // late-loading content grows scrollHeight past the first scroll.
      ;[100, 400, 900].forEach(delay => {
        setTimeout(() => {
          if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight
          }
        }, delay)
      })
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    const val = inputRef.current?.value?.trim()
    if (!val) return
    inputRef.current.value = ''
    onSend(val)
  }

  function handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e)
    }
  }

  function handleCopyClick(e) {
    // Track Book Now clicks from chat messages
    const link = e.target.closest('a')
    if (link && link.href && link.href.includes('/reservations/new') && typeof window.gtag === 'function') {
      window.gtag('event', 'chat_book_now_click')
    }

    const copyBtn = e.target.closest('.chat-link-copy')
    if (!copyBtn) return
    e.preventDefault()
    e.stopPropagation()

    const url = copyBtn.dataset.url
    if (!url) return

    navigator.clipboard.writeText(url).then(() => {
      const icon = copyBtn.querySelector('.copy-icon')
      const check = copyBtn.querySelector('.copy-check')
      if (icon) icon.classList.add('hidden')
      if (check) check.classList.remove('hidden')
      setTimeout(() => {
        if (icon) icon.classList.remove('hidden')
        if (check) check.classList.add('hidden')
      }, 1500)
    })
  }

  const remaining = maxMessages - messageCount
  const panelClasses = isOpen
    ? 'translate-y-0 opacity-100'
    : 'translate-y-full opacity-0 pointer-events-none'

  return (
    <div
      class={`fixed z-50 bottom-0 right-0 w-full transition-all duration-300 ease-out sm:bottom-6 sm:right-6 sm:w-96 sm:max-h-[calc(100vh-3rem)] max-h-screen ${panelClasses}`}
    >
      <div class="flex flex-col bg-white shadow-2xl overflow-hidden border border-gray-100 sm:rounded-2xl h-screen sm:h-[600px]">
        {/* Header */}
        <div class="flex items-center justify-between px-4 py-3 flex-shrink-0 bg-gradient-to-r from-[#1e3a8a] to-[#2d4ea0]">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-full flex items-center justify-center bg-[#c9a961] border-2 border-white/30">
              <span class="text-white text-sm font-bold">K</span>
            </div>
            <div>
              <h3 class="flex items-center gap-1.5 text-white text-sm font-semibold leading-tight">
                Kailani
                {/* Launching as a beta: a quiet label in the header, explained in the greeting */}
                <span
                  class="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider leading-none text-[#f3e3b5] bg-white/10 ring-1 ring-inset ring-[#c9a961]/70"
                  title="Kailani is new and still learning"
                >
                  Beta
                </span>
              </h3>
              <p class="text-blue-200 text-xs leading-tight">Vacation Planner</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            {remaining <= 5 && remaining > 0 && (
              <span class="text-xs text-blue-300">{remaining} messages remaining</span>
            )}
            {/* Only offered once there is something to start over from. */}
            {onReset && messages.length > 0 && (
              <button
                onClick={onReset}
                class="p-1 transition-colors text-white/70 hover:text-white"
                title="Start a new conversation"
                aria-label="Start a new conversation"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992V4.356m-4.993 4.992-2.74-2.74a7.5 7.5 0 1 0 1.73 7.77" />
                </svg>
              </button>
            )}
            <button onClick={onClose} class="p-1 transition-colors text-white/70 hover:text-white" aria-label="Close chat">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={messagesRef}
          class="flex-1 overflow-y-auto px-4 py-4 kailani-messages"
          style="scroll-behavior: smooth;"
          onClick={handleCopyClick}
        >
          {showGreeting && (
            <div class="mb-4">
              <div class="flex items-start mb-3 gap-2.5">
                <div class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#1e3a8a] to-[#c9a961] mt-0.5">
                  <span class="text-white text-xs font-bold">K</span>
                </div>
                <div class="bg-white rounded-2xl rounded-bl-md px-4 shadow-sm border border-gray-100 max-w-[85%] py-2.5">
                  {!redesign && (
                    <div>
                      <p class="text-sm leading-relaxed text-gray-800 m-0">
                        Aloha! I'm <strong class="font-semibold">Kailani</strong>, your Bennington Properties vacation concierge. How can I help you plan your Sunriver getaway?
                      </p>
                      <p class="text-xs leading-relaxed text-gray-500 mt-2 mb-0">
                        I'm new and still learning. If anything doesn't look right, our team is happy to help
                        {phone && ' at '}
                        {phone && (
                          <a href={`tel:${phoneTel || phone}`} class="whitespace-nowrap font-medium text-[#1e3a8a] underline decoration-[#1e3a8a]/30 hover:decoration-[#1e3a8a]">
                            {phone}
                          </a>
                        )}
                        .
                      </p>
                    </div>
                  )}
                  {/* The three questions are a list rather than a run-on sentence:
                      on a phone this bubble is about 40 characters wide, and the
                      inline version wrapped into an unreadable block. */}
                  {redesign && (
                  <div>
                  <p class="text-sm leading-relaxed text-gray-800 m-0">
                    Aloha and welcome to Bennington! I'm <strong class="font-semibold">Kailani</strong>, your AI vacation planner.
                  </p>
                  <p class="text-sm leading-relaxed text-gray-800 mt-2 mb-0">
                    My job is to help you find the perfect home and plan your ideal Sunriver getaway. To get started, share with me:
                  </p>
                  <ol class="mt-1.5 mb-0 pl-5 text-sm leading-relaxed text-gray-800 list-decimal">
                    <li>What dates are you coming?</li>
                    <li>How many people?</li>
                    <li class="mt-0.5">
                      Any other details?
                      <span class="text-gray-500"> Bedrooms, amenities, pet friendly, and so on.</span>
                    </li>
                  </ol>
                  <p class="text-xs leading-relaxed text-gray-500 mt-2.5 mb-0">
                    A quick note: I am an AI and I'm still learning. If you get stuck, or if you simply
                    prefer the local expertise of our human team, they can be reached at
                    {sms && (
                      <span>
                        {' '}text{' '}
                        <a href={`sms:${smsTel || sms}`} class="whitespace-nowrap font-medium text-[#1e3a8a] underline decoration-[#1e3a8a]/30 hover:decoration-[#1e3a8a]">
                          {sms}
                        </a>
                        ,
                      </span>
                    )}
                    {email && (
                      <span>
                        {' '}email{' '}
                        <a href={`mailto:${email}`} class="break-all font-medium text-[#1e3a8a] underline decoration-[#1e3a8a]/30 hover:decoration-[#1e3a8a]">
                          {email}
                        </a>
                        ,
                      </span>
                    )}
                    {phone && (
                      <span>
                        {' '}or call{' '}
                        <a href={`tel:${phoneTel || phone}`} class="whitespace-nowrap font-medium text-[#1e3a8a] underline decoration-[#1e3a8a]/30 hover:decoration-[#1e3a8a]">
                          {phone}
                        </a>
                      </span>
                    )}
                    .
                  </p>
                  </div>
                  )}
                </div>
              </div>
              <div class="ml-9 flex flex-wrap gap-2">
                {QUICK_ACTIONS.map(([label, message]) => (
                  <button
                    key={label}
                    onClick={() => onQuickAction(message)}
                    class="text-xs px-3 rounded-full border transition-all duration-200 py-1.5 border-[#1e3a8a]/20 text-[#1e3a8a] hover:bg-[#1e3a8a]/5 hover:border-[#1e3a8a]/40 hover:shadow-sm"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={msg.id || `msg-${i}`}>
              <Message message={msg} animate={false} onFeedback={onFeedback} />
              {msg.metadata?.property_cards && msg.metadata.property_cards.length > 0 && (
                <PropertyCards properties={msg.metadata.property_cards} searchSuffix={searchSuffix} />
              )}
            </div>
          ))}

          {/* Quick-reply suggestion chips after the latest reply */}
          {suggestions && suggestions.length > 0 && !isLoading && !isStreaming && (
            <div class="ml-9 mb-3 flex flex-wrap gap-2 chat-message animate-slide-up">
              {suggestions.map(label => (
                <button
                  key={label}
                  onClick={() => onSend(label)}
                  class="text-xs px-3 rounded-full border transition-all duration-200 py-1.5 border-[#1e3a8a]/20 text-[#1e3a8a] bg-white hover:bg-[#1e3a8a]/5 hover:border-[#1e3a8a]/40 hover:shadow-sm"
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Tool activity indicator */}
          {toolActivity && (
            <div class="flex items-start gap-2.5 mb-3 tool-activity animate-slide-up">
              <div class="w-7 h-7 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#c9a961] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-white text-xs font-bold">K</span>
              </div>
              <div class="bg-white rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm border border-gray-100">
                <p class="text-xs text-gray-500 m-0 flex items-center gap-2">
                  <svg class="w-3.5 h-3.5 animate-spin text-[#c9a961]" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {toolActivity.label}...
                </p>
              </div>
            </div>
          )}

          {/* Streaming content - ref-updated directly by App to avoid re-renders */}
          <div class={`flex items-start gap-2.5 mb-3 chat-message${isStreaming ? ' animate-slide-up' : ''}`} style={isStreaming ? '' : 'display:none'}>
            <div class="w-7 h-7 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#c9a961] flex items-center justify-center flex-shrink-0 mt-0.5">
              <span class="text-white text-xs font-bold">K</span>
            </div>
            <div class="chat-bubble-assistant max-w-[80%] bg-white rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm border border-gray-100">
              <div
                ref={streamElRef}
                class="text-sm leading-relaxed text-gray-800 streaming-content"
              />
            </div>
          </div>

          {/* Typing indicator */}
          {isLoading && !isStreaming && !toolActivity && (
            <div class="flex items-start gap-2.5 mb-3">
              <div class="w-7 h-7 rounded-full bg-gradient-to-br from-[#1e3a8a] to-[#c9a961] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span class="text-white text-xs font-bold">K</span>
              </div>
              <div class="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style="animation-delay: 0ms;" />
                  <div class="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style="animation-delay: 150ms;" />
                  <div class="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style="animation-delay: 300ms;" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div class="flex-shrink-0 border-t border-gray-100 bg-gray-50 px-4 py-3 sm:rounded-b-2xl">
          <form class="flex items-end gap-2" onSubmit={handleSubmit}>
            <div class="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about properties, dates, amenities..."
                autocomplete="off"
                disabled={inputDisabled}
                onKeydown={handleKeydown}
                class="w-full text-sm rounded-xl border border-gray-200 bg-white px-4 pr-10 transition-colors duration-200 py-2.5 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]/20 focus:border-[#1e3a8a]/40"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || inputDisabled}
              class="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0 bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white shadow-sm hover:shadow disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
              </svg>
            </button>
          </form>
          <p class="text-center mt-2 mb-0" style="font-size: 10px; color: #9ca3af;">
            Powered by Bennington Properties AI
          </p>
        </div>
      </div>
    </div>
  )
}
